import { useCallback, useEffect, useMemo, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import {
  UIBox,
  UIDivider,
  UIIconButton,
  UIInputAdornment,
  UIStack,
  UITextField,
  UITooltip,
  UITypography,
} from "../../../_app/components";
import { ContentCopy } from "../../../_app/components/icons";
import { useFeedbackAlerts } from "../../../_app/hooks";
import { associateTotp, enableMfa, verifyTotp } from "../../../auth/api";
import { useAuthSession, useMfaEnabled } from "../../../auth/hooks";
import { ERROR, SUCCESS } from "../../../auth/types";
import { createStylesheet } from "../../../_app/utils/styles";
import UISimpleDialog from "../../../_app/components/UISimpleDialog";
import UIInputOtp from "../../../_app/components/UIInputOtp";
import { useBrandName } from "../../../brand/hooks";

type Props = {
  open: boolean;
  onClose: () => void;
};

const EnableMFADialog = ({ open, onClose }: Props) => {
  const classes = useStyles();
  const { setFeedbackAlertSuccess, setFeedbackAlertError } = useFeedbackAlerts();
  const [isLoading, setIsLoading] = useState(false);
  const [totpSecret, setTotpSecret] = useState("");
  const { data: session } = useAuthSession();
  const accessToken = session?.accessToken ?? "";
  const brandName = useBrandName();

  const { data: hasMfaEnabled, refetch: refetchMfaEnabled } = useMfaEnabled();

  const reset = useCallback(() => {
    setIsLoading(false);
    setIsVerifying(false);
    setTotpSecret("");
    setTotpVerification("");
    setMfaConfirmed(false);
    setCopyTooltipOpen(false);
  }, []);

  // QR Code Setup
  useEffect(() => {
    const fetchTotpSecret = async () => {
      setIsLoading(true);
      const secret = await associateTotp(accessToken);
      if (secret) setTotpSecret(secret);
      else {
        setFeedbackAlertError("Fetching TOTP secret failed.");
        onClose();
      }
      setIsLoading(false);
    };

    if (open) fetchTotpSecret();
    else setTimeout(reset, 100);
  }, [open, onClose, session, reset, setFeedbackAlertError, accessToken]);

  const qrCodeValue = useMemo(
    () => `otpauth://totp/${brandName}:${session?.userProfile?.email}?secret=${totpSecret}&issuer=${brandName}`,
    [session?.userProfile?.email, totpSecret, brandName],
  );

  // OTP Code Validation
  const [isVerifying, setIsVerifying] = useState(false);
  const [totpVerification, setTotpVerification] = useState("");
  const totpVerificationSuccess = totpVerification === SUCCESS;
  const totpVerificationError = totpVerification === ERROR;
  const [mfaConfirmed, setMfaConfirmed] = useState(false);
  const [copyTooltipOpen, setCopyTooltipOpen] = useState(false);

  const handleOtpVerification = useCallback(
    async (code: string) => {
      setIsVerifying(true);
      const verified = await verifyTotp(accessToken, code);
      setTotpVerification(verified ?? "");
      setIsVerifying(false);
    },
    [accessToken],
  );

  const handleCopySecret = useCallback(() => {
    if (totpSecret) {
      (async () => {
        try {
          await navigator.clipboard.writeText(totpSecret);
          setCopyTooltipOpen(true);
          setTimeout(() => setCopyTooltipOpen(false), 2000);
        } catch (err) {
          console.error("Failed to copy text: ", err);
          // Fallback for browsers that don't support clipboard API
          const textArea = document.createElement("textarea");
          textArea.value = totpSecret;
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand("copy");
          document.body.removeChild(textArea);
          setCopyTooltipOpen(true);
          setTimeout(() => setCopyTooltipOpen(false), 2000);
        }
      })();
    }
  }, [totpSecret]);

  // MFA Enable
  const handleMfaEnable = async () => {
    setIsLoading(true);
    const confirmed = await enableMfa(accessToken);
    setMfaConfirmed(confirmed);
    if (confirmed) {
      setFeedbackAlertSuccess("MFA successfully enabled.");
      onClose();
      refetchMfaEnabled();
    } else setFeedbackAlertError("MFA couldn't be enabled, please try again later.");
    setIsLoading(false);
  };

  return (
    <UISimpleDialog
      open={open}
      maxWidth="sm"
      isLoading={isLoading}
      title={
        <UITypography variant="h3" textAlign="center" component="p">
          Multi-factor Authentication
        </UITypography>
      }
      actions={[
        { label: "Cancel", props: { variant: "outlined", onClick: onClose, disabled: isLoading } },
        {
          label: "Confirm MFA Setup",
          props: { onClick: () => void handleMfaEnable(), disabled: isLoading || !totpVerificationSuccess },
        },
      ]}
    >
      {!hasMfaEnabled && totpSecret && (
        <UIStack className={classes.container}>
          <UIBox className={classes.sectionBox}>
            <UITypography variant="h6" component="h2" className={classes.stepTitle}>
              Step 1: Add to Authenticator App
            </UITypography>
            <UITypography variant="body2" className={classes.stepDescription}>
              Scan the QR code below with your preferred authenticator app.
            </UITypography>
          </UIBox>

          {!mfaConfirmed && (
            <>
              <UIBox className={classes.qrCodeContainer}>
                <QRCodeSVG size={180} value={qrCodeValue} />
              </UIBox>

              <UIBox className={classes.sectionBox}>
                <UIDivider className={classes.dividerWithText}>
                  <UITypography variant="caption" color="text.secondary">
                    OR
                  </UITypography>
                </UIDivider>

                <UITypography variant="body2" className={classes.stepDescription}>
                  Alternatively, enter this secret key manually in your authenticator app:
                </UITypography>

                <UITextField
                  label="Secret Key"
                  value={totpSecret}
                  variant="outlined"
                  size="small"
                  fullWidth
                  className={classes.secretKeyField}
                  slotProps={{
                    input: {
                      readOnly: true,
                      endAdornment: (
                        <UIInputAdornment position="end">
                          <UITooltip
                            title={"Copied!"}
                            open={copyTooltipOpen}
                            onClose={() => setCopyTooltipOpen(false)}
                            disableHoverListener={copyTooltipOpen}
                            placement="top"
                          >
                            <UIIconButton onClick={handleCopySecret} edge="end" aria-label="Copy secret key">
                              <ContentCopy />
                            </UIIconButton>
                          </UITooltip>
                        </UIInputAdornment>
                      ),
                    },
                  }}
                />
              </UIBox>

              <UIBox className={classes.sectionBox}>
                <UIDivider className={classes.verificationSection} />

                <UITypography variant="h6" component="h2" className={classes.stepTitle}>
                  Step 2: Verify Setup
                </UITypography>

                <UITypography variant="body2" className={classes.stepDescription}>
                  Enter the 6-digit code from your authenticator app to verify the setup.
                </UITypography>

                <UIInputOtp
                  disabled={isVerifying}
                  onCompleted={handleOtpVerification}
                  success={totpVerificationSuccess}
                  error={totpVerificationError}
                />

                {totpVerificationSuccess && (
                  <UITypography variant="body2" color="success.main" className={classes.feedbackMessage}>
                    ✓ Code verified successfully! Click "Confirm MFA Setup" to complete.
                  </UITypography>
                )}

                {totpVerificationError && (
                  <UITypography variant="body2" color="error" className={classes.feedbackMessage}>
                    ✗ Verification failed. Please check the code and try again.
                  </UITypography>
                )}
              </UIBox>
            </>
          )}
        </UIStack>
      )}
    </UISimpleDialog>
  );
};

const useStyles = createStylesheet((theme) => ({
  container: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(4),
    width: "100%",
    flexDirection: "column",
  },
  sectionBox: {
    textAlign: "center",
    width: "100%",
  },
  stepTitle: {
    marginBottom: theme.spacing(1),
  },
  stepDescription: {
    marginBottom: theme.spacing(3),
    color: theme.palette.text.secondary,
  },
  qrCodeContainer: {
    textAlign: "center",
    width: "100%",
  },
  dividerWithText: {
    marginBottom: theme.spacing(2),
  },
  secretKeyField: {
    marginBottom: theme.spacing(3),
    "& .MuiInputBase-input": {
      fontFamily: "monospace",
      fontSize: "0.85rem",
      textAlign: "center",
    },
  },
  verificationSection: {
    marginBottom: theme.spacing(3),
  },
  feedbackMessage: {
    marginTop: theme.spacing(2),
  },
}));

export default EnableMFADialog;
